/**
 * EmptyState — Design System §5
 *
 * Pattern: icon + one sentence + one primary action.
 * Never a bare "No data" — always explain and offer a next step.
 *
 * Icon: pass a Material Symbols name string (e.g. "inbox") or any React node.
 * Single-word/underscore strings are rendered as Material Symbols (GAPS.md S6).
 */

import React from 'react';
import { Button, ButtonProps } from './Button.js';

export interface EmptyStateProps {
  /** Material Symbol name (e.g. "inbox") or any React node */
  icon?: React.ReactNode;
  /** Optional concise heading for a richer empty destination. */
  title?: React.ReactNode;
  /** Single descriptive sentence */
  message: string;
  /** Primary action */
  action?: {
    label: string;
    onClick: () => void;
    variant?: ButtonProps['variant'];
  };
  className?: string;
}

/** Heuristic: if the icon value looks like a Material Symbol name (lowercase words/underscores)
 *  render it as a Material Symbols ligature; otherwise pass through as-is. */
function isMaterialIconName(value: unknown): value is string {
  return typeof value === 'string' && /^[a-z][a-z0-9_]*$/.test(value);
}

export function EmptyState({ icon, title, message, action, className }: EmptyStateProps) {
  const iconNode = isMaterialIconName(icon) ? (
    <span
      aria-hidden="true"
      className="material-symbols-outlined"
      style={{ fontSize: '40px', lineHeight: 1, color: 'var(--color-text-disabled)' }}
    >
      {icon}
    </span>
  ) : icon ? (
    <span aria-hidden="true" style={{ fontSize: '40px', lineHeight: 1, color: 'var(--color-text-disabled)', display: 'block' }}>
      {icon}
    </span>
  ) : null;

  return (
    <div
      className={['ui-empty-state', className ?? ''].filter(Boolean).join(' ')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--empty-state-padding, var(--space-12) var(--space-6))',
        textAlign: 'center',
      }}
    >
      {iconNode}

      {title ? (
        <h2 style={{ margin: 0, fontSize: 'var(--text-h3-size)', lineHeight: 'var(--text-h3-lh)', color: 'var(--color-text)' }}>
          {title}
        </h2>
      ) : null}

      <p style={{
        margin: 0,
        fontSize: 'var(--text-body-size)',
        lineHeight: 'var(--text-body-lh)',
        color: 'var(--color-text-secondary)',
        maxWidth: '320px',
      }}>
        {message}
      </p>

      {action && (
        <Button variant={action.variant ?? 'primary'} size="md" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}
